import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { Store } from '@ngrx/store';
import { BehaviorSubject } from 'rxjs';
import { map } from 'rxjs/operators';

import { TranslocoService } from '@ngneat/transloco';

import { DialogService, DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';

import { LookupDialogDimensions } from '@core/interfaces';
import { EventService, fromList } from '@core/services';
import { dialogAttributes } from '@util/dialog';

import { endpoints } from '@environment';

import { ItemLookupRequest, ItemLookupResponseBody, lotRequiredCheckOnItemRequest, lotRequiredCheckOnItemResponseBody } from '#types/item';

import { CONSTANTS } from '@item/constants';
import { ItemLookupDialogComponent } from '@item/item-lookup-dialog/item-lookup-dialog.component';
import { ItemActions, fromItem } from '@item/state';

@Injectable({
  providedIn: 'root',
})
export class ItemService {
  itemLookupDialogSelectedCustomer$ = new BehaviorSubject<string>(null);
  itemLookupDialogAllowMultiple$ = new BehaviorSubject<boolean>(true);
  itemLookupDialogSelectionLimit$ = new BehaviorSubject<number>(null);

  itemLookupDialogDimensions$ = new BehaviorSubject<LookupDialogDimensions>({
    container: { width: 0, height: 0 },
    grid: { width: 0, height: 0 },
  });

  customerProductGroupList$ = this.store.select(fromList.selectCustomerProductGroupList);
  customerRateGroupList$ = this.store.select(fromList.selectCustomerRateGroupList);

  itemLotNumberCheck$ = this.store.select(fromItem.selectItemLotNumberCheck);

  facility: string;

  translate = (key: string | string[], params?: Record<string, string>, lang?: string) =>
    this.translocoService.translate(key, params, lang);

  constructor(
    private http: HttpClient,
    private store: Store,
    private translocoService: TranslocoService,
    private dialogService: DialogService,
    private eventService: EventService
  ) {}

  requestLists() {
    this.store.dispatch(ItemActions.RequestLists());
  }

  requestCustomerProductGroupList(custid: string) {
    this.store.dispatch(ItemActions.RequestItemCustomerProductGroupList({ custid }));
  }

  requestCustomerRateGroupList(custid: string) {
    this.store.dispatch(ItemActions.RequestItemCustomerRateGroupList({ custid }));
  }

  getItemLookup(payload: ItemLookupRequest) {
    return this.http.post(endpoints.item.itemLookup, payload, { withCredentials: true }).pipe(
      this.eventService.indicate(CONSTANTS.GRID_ITEM_LOOKUP_DIALOG),
      map((response: ItemLookupResponseBody) => response)
    );
  }

  getItemLotRequiredCheck(payload: lotRequiredCheckOnItemRequest) {
    return this.http.post(endpoints.item.itemLotRequiredCheck, payload, { withCredentials: true }).pipe(
      this.eventService.indicate(CONSTANTS.ITEM_LOT_REQUIRED_CHECK),
      map((response: lotRequiredCheckOnItemResponseBody) => response)
    );
  }

  itemLookupDialog(
    custid: string = null,
    allowMultipleValues: boolean = false,
    selectionLimit: number = null,
    config: DynamicDialogConfig = {}
  ): DynamicDialogRef {
    this.itemLookupDialogSelectedCustomer$.next(custid);
    this.itemLookupDialogAllowMultiple$.next(allowMultipleValues);
    this.itemLookupDialogSelectionLimit$.next(selectionLimit);

    return this.dialogService.open(ItemLookupDialogComponent, {
      ...dialogAttributes,
      header: this.translate('item.item_lookup_dialog.dialog.title'),
      styleClass: 'lookup-dialog large-dialog',
      ...config,
    });
  }
}
